// =============================================================================
// RouteErrorBoundary - keeps one page's failure inside that page.
//
// Without a boundary, any error while rendering a view (or a page bundle that
// failed to load) unmounted the whole app and left a blank white screen.
// Wrapped around the routed view in Layout (keyed by path, so navigating away
// clears it) and around the public routes in App. A stale-bundle error
// reloads once (lib/chunkReload); anything else shows a small card with
// Reload and Go to dashboard, and the sidebar stays usable.
//
// A class component on purpose: React only supports error boundaries as
// classes (getDerivedStateFromError / componentDidCatch).
// =============================================================================
import { Component } from "react";
import { C } from "../lib/tokens";
import { Btn } from "./ui";
import { isChunkLoadError, reloadForNewVersion } from "../lib/chunkReload";

export default class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, reloading: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    if (isChunkLoadError(error) && reloadForNewVersion()) {
      this.setState({ reloading: true });
      return;
    }
    console.error("[RouteErrorBoundary]", error, info && info.componentStack);
  }

  render() {
    const { error, reloading } = this.state;
    if (!error) return this.props.children;
    const stale = isChunkLoadError(error);
    return (
      <div role="alert" data-testid="route-error" style={{ padding: 24, display: "flex", justifyContent: "center" }}>
        <div style={{
          maxWidth: 460, width: "100%", boxSizing: "border-box", padding: 20, borderRadius: 12,
          background: C.bgPrimary, border: "0.5px solid " + C.borderMid, color: C.textPrimary,
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
            {reloading ? "Updating PracticeOS..." : stale ? "PracticeOS was updated" : "This page did not load"}
          </div>
          <div style={{ fontSize: 13, color: C.textSecondary, lineHeight: 1.5, marginBottom: 14 }}>
            {reloading
              ? "Loading the latest version."
              : stale
                ? "A new version is available. Reload to continue; nothing you saved is lost."
                : "Something went wrong on this page. Reload to try again, or go back to the dashboard. The rest of PracticeOS still works."}
          </div>
          {!reloading && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Btn variant="primary" onClick={() => window.location.reload()}>Reload</Btn>
              <Btn variant="outline" onClick={() => { window.location.href = "/dashboard"; }}>Go to dashboard</Btn>
            </div>
          )}
        </div>
      </div>
    );
  }
}

